import React, { useCallback, useMemo, useEffect } from "react";
import { ItemsContainer, StyledProfileMenu } from "./index-styled";
import Item from "_c/modules/tasks/TaskItem";
import { Helmet } from "_c/Helmet";
import { useTranslation } from "react-i18next";
import { ORDERS_NS as ns } from "@/i18n/ns";
import TaskEditorModal from "_c/Modals/task-editor/modal";
import qs from "query-string";
import MobileBottomBar from "./MobileBottomBar/container";
import DesktopTopBar from "./DesktopTopBar/container";
import { MobileUiVars } from "./ui-vars";
import { filterObject } from "@/utils";
import { FullScreenSpin as Spin } from "_c/preloader";
import { Row, Col } from "reactstrap";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Redirect } from "react-router";
import { useHistory, useParams } from "react-router-dom";
import AuthOnly from "_c/AuthOnly";
import { useClientProfile, useTasksQuery, useTaskEditorState } from "./hooks";
import ClientProfileModal from "_c/Modals/client-profile-modal";
import { useParsedQuery } from "@/hooks/useParsedQuery";
import { useToggle } from "@/hooks/useToggle";
import Filters from "./Filters";
import CreateOrderFab from "./CreateOrderFab";
import OnboardingModal from "_c/Modals/onboarding";
import InfiniteScrollList from "_c/InfiniteScrollList";
import isempty from "lodash/isEmpty";
import Empty from "_c/Empty";
import { scrollToTop } from "@/utils";

export const OrdersPage = (props) => {
  const { clientProfileState, openClientProfile, closeClientProfile } =
    useClientProfile();
  const [filtersOpen, toggleFilters] = useToggle();
  const taskEditorState = useTaskEditorState();
  const { mode } = useParams();
  const history = useHistory();
  const tasksQuery = useTasksQuery(mode);
  const parsedQuery = useParsedQuery();
  const scrollKey = useMemo(
    () => JSON.stringify([mode, parsedQuery]),
    [parsedQuery]
  );
  useEffect(scrollToTop, [scrollKey]);
  const { t, ready } = useTranslation(["common", "errors", ns], {
    useSuspense: false,
  });
  const updateQuery = useCallback(
    (partialQuery) => {
      history.push(
        qs.stringifyUrl({
          url: props.routePrefix + "/orders/" + mode,
          query: filterObject(
            {
              ...parsedQuery,
              ...partialQuery,
            },
            (key) =>
              key !== null &&
              key !== undefined &&
              key !== false &&
              key !== "false" &&
              key !== ""
          ),
        })
      );
    },
    [parsedQuery, mode, props.routePrefix]
  );

  const isMobile = useMediaQuery({ maxWidth: MobileUiVars.mobileBp });

  const openTaskEditor = useCallback((task) => {
    taskEditorState.open({ task });
  }, []);
  const closeTaskEditor = useCallback(() => {
    taskEditorState.close();
  }, []);
  if (!mode) {
    return <Redirect to={`${props.routePrefix}/orders/all`} />;
  }
  if (!ready) return <Spin size="xl" />;
  const renderPages = (pages) => {
    if (isempty(pages?.[0]?.tasks)) {
      return <Empty message={t("common:empty")} />;
    }
    return (
      <ItemsContainer fluid>
        <Row>
          {pages.map((page) =>
            page.tasks.map((task) => (
              <Col style={{ display: "flex" }} key={task._id} lg={6}>
                <Item
                  css={`
                    margin-bottom: 20px;
                    box-shadow: 0px 6px 20px var(--color-primary);
                    @media (max-width: 991px) {
                      box-shadow: none;
                      margin-bottom: 10px;
                    }
                  `}
                  onTaskUpdate={props.onTaskUpdate}
                  onTaskRemove={props.onTaskRemove}
                  openClientProfile={openClientProfile}
                  key={task._id}
                  openEditor={openTaskEditor}
                  t={t}
                  task={task}
                />
              </Col>
            ))
          )}
        </Row>
      </ItemsContainer>
    );
  };
  return (
    <AuthOnly>
      <div id="orders-wrap">
        {isMobile && (
          <MobileBottomBar
            openFilters={toggleFilters}
            updateQuery={updateQuery}
            t={t}
            query={parsedQuery}
          />
        )}
        <Helmet title={t(`${ns}:page_title`)} />
        <TaskEditorModal
          currency={props.currency}
          isOpen={taskEditorState.isOpen}
          task={taskEditorState.task}
          personal={taskEditorState.personal}
          onClose={closeTaskEditor}
          masterId={taskEditorState.state?.master || props.masterId}
          mode={
            taskEditorState.task
              ? taskEditorState.task.custom
                ? "custom"
                : "normal"
              : "custom"
          }
        />
        <StyledProfileMenu t={t} items={props.menu} />
        {!isMobile && (
          <DesktopTopBar
            openTaskEditor={openTaskEditor}
            openFilters={toggleFilters}
            updateQuery={updateQuery}
            query={parsedQuery}
            t={t}
          />
        )}
        <InfiniteScrollList query={tasksQuery}>
          {renderPages}
        </InfiniteScrollList>
      </div>
      {props.isMaster && (
        <ClientProfileModal
          disableActions
          t={t}
          client={clientProfileState.client}
          isOpen={clientProfileState.isOpen}
          onClose={closeClientProfile}
        />
      )}
      <Filters
        isOpen={filtersOpen}
        onClose={toggleFilters}
        updateQuery={updateQuery}
        query={parsedQuery}
        t={t}
      />
      {!isMobile && props.isBiz && (
        <CreateOrderFab t={t} openTaskEditor={taskEditorState.open} />
      )}
      {props.isMaster && <OnboardingModal domain="bookings" />}
    </AuthOnly>
  );
};

OrdersPage.displayName = "OrdersPage";

export default OrdersPage;
